import { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  CircularProgress,
  Alert,
  FormHelperText
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useAuthStore } from '../../store/authStore';

const AdminManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const { user: currentUser } = useAuthStore();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [adminToUpdate, setAdminToUpdate] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [updateLoading, setUpdateLoading] = useState(false);
  
  // New admin dialog
  const [newAdminDialogOpen, setNewAdminDialogOpen] = useState(false);
  const [newAdminData, setNewAdminData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    role: 'admin'
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // Fetch admins
  useEffect(() => {
    const fetchAdmins = async () => {
      try {
        setLoading(true);
        setError(null);
        const { data } = await axios.get('/api/users');
        // Filter only admin and superadmin users
        const adminUsers = data.filter(user => user.role === 'admin' || user.role === 'superadmin');
        setAdmins(adminUsers);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching admins:', error);
        setError('Ошибка при загрузке администраторов');
        setLoading(false);
      }
    };

    fetchAdmins();
  }, []);

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle search
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  // Handle role filter change
  const handleRoleFilterChange = (event) => {
    setRoleFilter(event.target.value);
    setPage(0);
  };

  // Filter admins based on search query and role filter
  const filteredAdmins = admins.filter((admin) => {
    const searchStr = searchQuery.toLowerCase();
    const matchesSearch = 
      admin.name.toLowerCase().includes(searchStr) ||
      admin.email.toLowerCase().includes(searchStr) ||
      (admin.phone && admin.phone.toLowerCase().includes(searchStr));
    
    const matchesRole = roleFilter === '' || admin.role === roleFilter;
    
    return matchesSearch && matchesRole;
  });

  // Calculate pagination
  const paginatedAdmins = filteredAdmins.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Open delete dialog
  const handleOpenDeleteDialog = (admin) => {
    setAdminToDelete(admin);
    setDeleteDialogOpen(true);
  };

  // Close delete dialog
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setAdminToDelete(null);
  };

  // Handle admin deletion
  const handleDeleteAdmin = async () => {
    if (!adminToDelete) return;

    try {
      setDeleteLoading(true);
      await axios.delete(`/api/users/${adminToDelete.id}`);
      setAdmins(admins.filter(a => a.id !== adminToDelete.id));
      setDeleteLoading(false);
      handleCloseDeleteDialog();
    } catch (error) {
      console.error('Error deleting admin:', error);
      setError('Ошибка при удалении администратора');
      setDeleteLoading(false);
    }
  };

  // Open role update dialog
  const handleOpenRoleDialog = (admin) => {
    setAdminToUpdate(admin);
    setNewRole(admin.role);
    setRoleDialogOpen(true);
  };

  // Close role update dialog
  const handleCloseRoleDialog = () => {
    setRoleDialogOpen(false);
    setAdminToUpdate(null);
    setNewRole('');
  };

  // Handle role change
  const handleRoleChange = (event) => {
    setNewRole(event.target.value);
  };

  // Handle admin role update
  const handleUpdateAdminRole = async () => {
    if (!adminToUpdate || !newRole) return;

    try {
      setUpdateLoading(true);
      await axios.put(`/api/users/${adminToUpdate.id}`, { role: newRole });
      setAdmins(admins.map(a => 
        a.id === adminToUpdate.id ? { ...a, role: newRole } : a
      ));
      setUpdateLoading(false);
      handleCloseRoleDialog();
    } catch (error) {
      console.error('Error updating admin role:', error);
      setError('Ошибка при обновлении роли администратора');
      setUpdateLoading(false);
    }
  };

  // Handle new admin data change
  const handleNewAdminDataChange = (e) => {
    const { name, value } = e.target;
    setNewAdminData({
      ...newAdminData,
      [name]: value
    });
    
    // Clear error for this field if it exists
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: undefined
      });
    }
  };

  // Handle create new admin
  const handleCreateAdmin = async () => {
    // Validate form
    const errors = {};
    if (!newAdminData.name) errors.name = 'Имя обязательно';
    if (!newAdminData.email) errors.email = 'Email обязателен';
    if (!/^\S+@\S+\.\S+$/.test(newAdminData.email)) errors.email = 'Некорректный email';
    if (!newAdminData.password) errors.password = 'Пароль обязателен';
    if (newAdminData.password.length < 6) errors.password = 'Пароль должен быть не менее 6 символов';
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    try {
      setCreateLoading(true);
      const { data } = await axios.post('/api/users', newAdminData);
      setAdmins([...admins, data]);
      setCreateLoading(false);
      setNewAdminDialogOpen(false);
      setNewAdminData({
        name: '',
        email: '',
        password: '',
        phone: '',
        role: 'admin'
      });
      setFormErrors({});
    } catch (error) {
      console.error('Error creating admin:', error);
      setError('Ошибка при создании администратора');
      setCreateLoading(false);
      if (error.response && error.response.data) {
        if (error.response.data.message === 'User already exists') {
          setFormErrors({...formErrors, email: 'Пользователь с таким email уже существует'});
        }
      }
    }
  };

  // Get role chip color and label
  const getRoleChip = (role) => {
    switch (role) {
      case 'superadmin':
        return <Chip label="Суперадмин" color="error" size="small" />;
      case 'admin':
        return <Chip label="Администратор" color="primary" size="small" />;
      default:
        return <Chip label={role} size="small" />;
    }
  };

  // Get admin avatar
  const getAdminAvatar = (admin) => {
    return (
      <Avatar sx={{ bgcolor: stringToColor(admin.name) }}>
        {admin.name.charAt(0).toUpperCase()}
      </Avatar>
    );
  };

  // Generate color from string
  const stringToColor = (string) => {
    let hash = 0;
    for (let i = 0; i < string.length; i++) {
      hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (let i = 0; i < 3; i++) {
      const value = (hash >> (i * 8)) & 0xFF;
      color += ('00' + value.toString(16)).substr(-2);
    }
    return color;
  };

  // Check if current user can edit the target admin
  const canEditAdmin = (targetAdmin) => {
    // Only superadmin can edit other admins
    if (currentUser.role === 'superadmin' && currentUser.id !== targetAdmin.id) {
      return true;
    }
    
    return false;
  };

  // Check if current user can delete the target admin
  const canDeleteAdmin = (targetAdmin) => {
    // Only superadmin can delete other admins
    if (currentUser.role === 'superadmin' && currentUser.id !== targetAdmin.id) {
      return true;
    }
    
    return false;
  };

  // Check if current user can create admins
  const canCreateAdmin = () => {
    return currentUser?.role === 'superadmin';
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        gap: { xs: 2, sm: 0 },
        mb: { xs: 2, sm: 3 } 
      }}>
        <Typography 
          variant="h4"
          sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}
        >
          Управление администраторами
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<PersonAddIcon />}
          onClick={() => setNewAdminDialogOpen(true)}
          disabled={!canCreateAdmin()}
        >
          Добавить администратора
        </Button>
      </Box>

      <Paper sx={{ width: '100%', mb: { xs: 1.5, sm: 2 }, borderRadius: { xs: 1, sm: 2 } }}>
        <Box sx={{ 
          p: { xs: 1.5, sm: 2 }, 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1.5, sm: 2 }
        }}>
          <TextField
            variant="outlined"
            placeholder="Поиск по имени, email, телефону..."
            value={searchQuery}
            onChange={handleSearchChange}
            size={isMobile ? "small" : "medium"}
            sx={{ flexGrow: 1, width: { xs: '100%', sm: 'auto' } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          
          <FormControl sx={{ minWidth: { xs: '100%', sm: 200 } }}>
            <InputLabel id="role-filter-label">Роль</InputLabel>
            <Select
              labelId="role-filter-label"
              value={roleFilter}
              label="Роль"
              onChange={handleRoleFilterChange}
            >
              <MenuItem value="">Все</MenuItem>
              <MenuItem value="admin">Администратор</MenuItem>
              <MenuItem value="superadmin">Суперадмин</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mx: 2, mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer sx={{ 
              overflowX: 'auto',
              '&::-webkit-scrollbar': {
                height: 8
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: theme.palette.primary.light,
                borderRadius: 4
              }
            }}>
              <Table size={isMobile ? 'small' : 'medium'}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ p: { xs: 1, sm: 2 } }}></TableCell>
                    <TableCell sx={{ p: { xs: 1, sm: 2 }, whiteSpace: 'nowrap' }}>Имя</TableCell>
                    <TableCell sx={{ p: { xs: 1, sm: 2 }, whiteSpace: 'nowrap' }}>Email</TableCell>
                    <TableCell sx={{ p: { xs: 1, sm: 2 }, whiteSpace: 'nowrap', display: { xs: 'none', sm: 'table-cell' } }}>Телефон</TableCell>
                    <TableCell sx={{ p: { xs: 1, sm: 2 }, whiteSpace: 'nowrap' }}>Роль</TableCell>
                    <TableCell sx={{ p: { xs: 1, sm: 2 }, whiteSpace: 'nowrap', display: { xs: 'none', md: 'table-cell' } }}>Дата регистрации</TableCell>
                    <TableCell sx={{ p: { xs: 1, sm: 2 }, whiteSpace: 'nowrap' }} align="right">Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedAdmins.length > 0 ? (
                    paginatedAdmins.map((admin) => (
                      <TableRow key={admin.id}>
                        <TableCell sx={{ p: { xs: 1, sm: 2 } }}>{getAdminAvatar(admin)}</TableCell>
                        <TableCell sx={{ p: { xs: 1, sm: 2 } }}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: { xs: 80, sm: 150 } }}>
                            {admin.name}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ p: { xs: 1, sm: 2 } }}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: { xs: 100, sm: 200 } }}>
                            {admin.email}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ p: { xs: 1, sm: 2 }, display: { xs: 'none', sm: 'table-cell' } }}>{admin.phone || '-'}</TableCell>
                        <TableCell sx={{ p: { xs: 1, sm: 2 } }}>
                          <Chip
                            label={admin.role === 'superadmin' ? 'Суперадмин' : 'Администратор'}
                            color={admin.role === 'superadmin' ? 'error' : 'primary'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell sx={{ p: { xs: 1, sm: 2 }, display: { xs: 'none', md: 'table-cell' } }}>{new Date(admin.createdAt).toLocaleDateString()}</TableCell>
                        <TableCell align="right" sx={{ p: { xs: 1, sm: 2 } }}>
                          {canEditAdmin(admin) && (
                            <IconButton 
                              color="primary"
                              onClick={() => handleOpenRoleDialog(admin)}
                            >
                              <EditIcon />
                            </IconButton>
                          )}
                          {canDeleteAdmin(admin) && (
                            <IconButton 
                              color="error"
                              onClick={() => handleOpenDeleteDialog(admin)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        {filteredAdmins.length === 0 ? 'Нет администраторов' : 'Нет результатов по вашему запросу'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={isMobile ? [5, 10] : [5, 10, 25]}
              component="div"
              count={filteredAdmins.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage={isMobile ? "Строк:" : "Строк на странице:"}
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} из ${count}`}
            />
          </>
        )}
      </Paper>

      {/* Delete Admin Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
      >
        <DialogTitle>Удаление администратора</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Вы уверены, что хотите удалить администратора {adminToDelete?.name}? Это действие нельзя отменить.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={deleteLoading}>
            Отмена
          </Button>
          <Button 
            onClick={handleDeleteAdmin} 
            color="error" 
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={20} /> : null}
          >
            Удалить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Role Dialog */}
      <Dialog
        open={roleDialogOpen}
        onClose={handleCloseRoleDialog}
      >
        <DialogTitle>Изменение роли</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Изменение роли для пользователя {adminToUpdate?.name}
          </DialogContentText>
          <FormControl fullWidth>
            <InputLabel id="role-select-label">Роль</InputLabel>
            <Select
              labelId="role-select-label"
              value={newRole}
              label="Роль"
              onChange={handleRoleChange}
            >
              <MenuItem value="admin">Администратор</MenuItem>
              <MenuItem value="superadmin">Суперадмин</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRoleDialog} disabled={updateLoading}>
            Отмена
          </Button>
          <Button 
            onClick={handleUpdateAdminRole} 
            color="primary" 
            disabled={updateLoading || newRole === adminToUpdate?.role}
            startIcon={updateLoading ? <CircularProgress size={20} /> : null}
          >
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Admin Dialog */}
      <Dialog
        open={newAdminDialogOpen}
        onClose={() => !createLoading && setNewAdminDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Создание нового администратора</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Заполните форму для создания нового администратора
          </DialogContentText>
          
          <TextField
            margin="dense"
            label="Имя"
            type="text"
            fullWidth
            variant="outlined"
            name="name"
            value={newAdminData.name}
            onChange={handleNewAdminDataChange}
            error={!!formErrors.name}
            helperText={formErrors.name}
            sx={{ mb: 2 }}
          />
          
          <TextField
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            variant="outlined"
            name="email"
            value={newAdminData.email}
            onChange={handleNewAdminDataChange}
            error={!!formErrors.email}
            helperText={formErrors.email}
            sx={{ mb: 2 }}
          />
          
          <TextField
            margin="dense"
            label="Пароль"
            type="password"
            fullWidth
            variant="outlined"
            name="password"
            value={newAdminData.password}
            onChange={handleNewAdminDataChange}
            error={!!formErrors.password}
            helperText={formErrors.password}
            sx={{ mb: 2 }}
          />
          
          <TextField
            margin="dense"
            label="Телефон (необязательно)"
            type="tel"
            fullWidth
            variant="outlined"
            name="phone"
            value={newAdminData.phone}
            onChange={handleNewAdminDataChange}
            sx={{ mb: 2 }}
          />
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="new-admin-role-label">Роль</InputLabel>
            <Select
              labelId="new-admin-role-label"
              name="role"
              value={newAdminData.role}
              label="Роль"
              onChange={handleNewAdminDataChange}
            >
              <MenuItem value="admin">Администратор</MenuItem>
              {currentUser?.role === 'superadmin' && (
                <MenuItem value="superadmin">Суперадмин</MenuItem>
              )}
            </Select>
            <FormHelperText>
              {newAdminData.role === 'admin' ? 
                'Администратор может управлять контентом, но не может создавать других администраторов' : 
                'Суперадмин имеет полный доступ ко всем функциям, включая создание других администраторов'}
            </FormHelperText>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setNewAdminDialogOpen(false)} 
            disabled={createLoading}
          >
            Отмена
          </Button>
          <Button 
            onClick={handleCreateAdmin} 
            color="primary" 
            disabled={createLoading}
            startIcon={createLoading ? <CircularProgress size={20} /> : null}
          >
            Создать
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminManagement;
